// Gates the manager-only pages (offer authoring). Stacks AFTER `auth`, which owns
// the not-logged-in case — so by the time this runs there is a customer and the
// only question left is whether they are a manager.
//
// A logged-in non-manager gets a **404, not a redirect**: the page is not theirs
// to know exists, which is the same rule the backend applies to the offer
// endpoints (a manager asking for another manager's offer gets 404 rather than
// 403 — which offer numbers exist is not their business either). It is also why
// this is not a redirect to the account page: that would confirm the URL is real.
//
// `plugins/customer-load.ts` resolves the customer during SSR and plugins run
// before route middleware, so this decides the same way on the server and on the
// client and the 404 is server-rendered rather than flashing the page first.
export default defineNuxtRouteMiddleware(() => {
  // Destructured plain values, as `auth.ts` does: middleware is evaluated once per
  // navigation, so there is nothing here to stay reactive to.
  const { customer, isLoggedIn } = useCustomerStore()

  // Not logged in at all — let `auth` do its redirect rather than 404ing someone
  // who simply has no session yet.
  if (!isLoggedIn) return

  if (!customer?.is_manager) {
    // `useI18n()` is a setup-only composable and throws "Must be called at the top
    // of a `setup` function" here; the injected `$i18n` is the one that works
    // outside a component, which is also what `services/api.ts` uses.
    const { $i18n } = useNuxtApp()
    throw createError({ statusCode: 404, statusMessage: $i18n.t('errors.pageNotFound'), fatal: true })
  }
})
